import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSnackBar } from '@/components/feedback/SnackBar'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { useAuth } from '@/hooks/useAuth'
import { canStartHubs } from '@/features/auth/utils/verification'
import {
  fetchCallToken,
  getCallHub,
  type CallHubClient,
} from '@/features/calls/api/callHub'
import { IncomingCallOverlay, type IncomingCall } from './IncomingCallOverlay'
import { VoiceCallScreen, type ActiveCall } from './VoiceCallScreen'

interface OutgoingCall {
  callId: string | null
  toUserId: string
  pricePerMinute: number
  withVideo: boolean
}

interface CallContextValue {
  incoming: IncomingCall | null
  outgoing: OutgoingCall | null
  active: ActiveCall | null
  invite: (toUserId: string) => Promise<void>
  accept: (callId: string) => Promise<void>
  reject: (callId: string) => Promise<void>
  cancel: (callId: string | null) => Promise<void>
  end: (callId: string) => Promise<void>
}

const CallContext = createContext<CallContextValue | null>(null)

export function useCall(): CallContextValue {
  const api = useContext(CallContext)
  if (!api) throw new Error('useCall must be used inside a CallProvider')
  return api
}

interface CallProviderProps {
  children: ReactNode
  /** Injectable transport; defaults to the app-scoped singleton. */
  hub?: CallHubClient
}

/**
 * Global voice-call listener (mounted once in `AppLayout`, next to the
 * snackbar host — the same global-mount spot as the notification/social
 * hub listeners). Owns the signaling connection for verified sessions,
 * the incoming sheet, the outgoing ring and the active voice screen.
 */
export function CallProvider({ children, hub }: CallProviderProps) {
  const { user, isLoading } = useAuth()
  const { strings } = useLanguage()
  const copy = strings.calls
  const { showSnackBar } = useSnackBar()
  const [incoming, setIncoming] = useState<IncomingCall | null>(null)
  const [outgoing, setOutgoing] = useState<OutgoingCall | null>(null)
  const [active, setActive] = useState<ActiveCall | null>(null)
  const stateRef = useRef({ incoming, outgoing, active })
  stateRef.current = { incoming, outgoing, active }
  const stringsRef = useRef(copy)
  stringsRef.current = copy

  const transport = useMemo(() => hub ?? getCallHub(), [hub])

  const joinActive = useCallback(
    async (callId: string, peerName: string, pricePerMinute: number, withVideo: boolean) => {
      try {
        const token = await fetchCallToken(callId)
        setActive({ callId, token: token.token, wsUrl: token.wsUrl, pricePerMinute, peerName, withVideo })
      } catch {
        showSnackBar({ content: stringsRef.current.joinFailed })
        setIncoming((cur) => (cur?.callId === callId ? null : cur))
        setOutgoing((cur) => (cur?.callId === callId ? null : cur))
      }
    },
    [showSnackBar],
  )

  useEffect(() => {
    if (isLoading || !canStartHubs(user)) return
    const client = transport
    client.setEvents({
      onIncoming: (data) =>
        setIncoming({
          callId: data.callId,
          fromUserId: data.fromUserId,
          pricePerMinute: data.pricePerMinute,
          withVideo: data.withVideo,
        }),
      onInvited: (data) =>
        setOutgoing({
          callId: data.callId,
          toUserId: data.toUserId,
          pricePerMinute: data.pricePerMinute,
          withVideo: data.withVideo,
        }),
      onStarted: (data) => {
        const { incoming: inc, outgoing: out } = stateRef.current
        const peer =
          inc?.callId === data.callId
            ? (inc.fromName ?? inc.fromUserId.slice(0, 8))
            : out?.callId === data.callId || out?.callId === null
              ? out.toUserId.slice(0, 8)
              : data.callId.slice(0, 8)
        const price =
          inc?.callId === data.callId ? inc.pricePerMinute : (out?.pricePerMinute ?? 0)
        const withVideo =
          inc?.callId === data.callId ? (inc.withVideo ?? false) : (out?.withVideo ?? false)
        setIncoming((cur) => (cur?.callId === data.callId ? null : cur))
        setOutgoing((cur) => (cur?.callId === data.callId || cur?.callId === null ? null : cur))
        void joinActive(data.callId, peer, price, withVideo)
      },
      onRejected: (data) => {
        setOutgoing((cur) => (cur?.callId === data.callId ? null : cur))
        showSnackBar({ content: stringsRef.current.declined })
      },
      onCancelled: (data) => {
        setIncoming((cur) => (cur?.callId === data.callId ? null : cur))
        showSnackBar({ content: stringsRef.current.cancelled })
      },
      onEnded: () => {
        setActive(null)
        setOutgoing(null)
        showSnackBar({ content: stringsRef.current.ended })
      },
      onError: (error) => {
        const message =
          error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
            ? error.message
            : stringsRef.current.joinFailed
        showSnackBar({ content: message })
      },
    })
    void client.connect().catch(() => {})
    return () => {
      void client.disconnect()
    }
  }, [isLoading, user, transport, joinActive, showSnackBar])

  const invite = useCallback(
    async (toUserId: string) => {
      await transport.invite(toUserId)
    },
    [transport],
  )

  const accept = useCallback(
    async (callId: string) => {
      await transport.accept(callId)
    },
    [transport],
  )

  const reject = useCallback(
    async (callId: string) => {
      try {
        await transport.reject(callId)
      } finally {
        setIncoming((cur) => (cur?.callId === callId ? null : cur))
      }
    },
    [transport],
  )

  const cancel = useCallback(
    async (callId: string | null) => {
      try {
        if (callId) await transport.cancel(callId)
      } finally {
        setOutgoing(null)
      }
    },
    [transport],
  )

  const end = useCallback(
    async (callId: string) => {
      try {
        await transport.end(callId)
      } finally {
        // The `CallEnded` broadcast clears the room too; clearing here
        // covers calls that never reached a shared group.
        setActive((cur) => (cur?.callId === callId ? null : cur))
        setOutgoing((cur) => (cur?.callId === callId ? null : cur))
      }
    },
    [transport],
  )

  const missed = useCallback(
    (callId: string) => {
      setIncoming((cur) => (cur?.callId === callId ? null : cur))
      showSnackBar({ content: stringsRef.current.missed })
    },
    [showSnackBar],
  )

  const value = useMemo<CallContextValue>(
    () => ({ incoming, outgoing, active, invite, accept, reject, cancel, end }),
    [incoming, outgoing, active, invite, accept, reject, cancel, end],
  )

  return (
    <CallContext.Provider value={value}>
      {children}
      <IncomingCallOverlay
        call={incoming}
        onAccept={(id) => void accept(id).catch(() => {})}
        onReject={(id) => void reject(id)}
        onMissed={missed}
      />
      {outgoing && !active && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 px-6 pb-[env(safe-area-inset-bottom)]"
          role="dialog"
          aria-label={outgoing.withVideo ? copy.callingVideo : copy.calling}
        >
          <div className="mb-8 w-full max-w-sm rounded-[20px] bg-lm-surface-container p-5 text-center">
            <h2 className="text-headline-sm font-bold text-lm-on-surface">
              {outgoing.withVideo ? copy.callingVideo : copy.calling}
            </h2>
            <p className="mt-1 text-title-md text-lm-on-surface">{outgoing.toUserId.slice(0, 8)}</p>
            <button
              type="button"
              onClick={() => void cancel(outgoing.callId)}
              className="mt-4 h-12 w-full rounded-full bg-lm-error-container text-title-sm font-semibold text-lm-on-error-container"
            >
              {strings.common.cancel}
            </button>
          </div>
        </div>
      )}
      {active && <VoiceCallScreen call={active} onEnd={(id) => void end(id)} />}
    </CallContext.Provider>
  )
}
